import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router'
import { useCoreContext } from '../../utils/core/RPPCoreContext'
import { useGetTotemEntityQuery } from '../../__codegen__/api'
import { useDashMessages } from '../../intl/intl.hooks'

export const useTotemInfo = () => {
  const { totemInfoByRoute } = useCoreContext()
  const { pathname } = useLocation()

  const totemInfo = useMemo(() => {
    const matchedKey = Object.keys(totemInfoByRoute).find(key => {
      if (matchPath(pathname, { path: key, exact: true })?.path) {
        return true
      }
    })
    return totemInfoByRoute[matchedKey]
  }, [pathname, totemInfoByRoute])

  return totemInfo
}

interface TotemEntityMetadata {
  ipp_tool_name_override?: string
}

/**
 * Strips redundant suffixes from tool names for cleaner display.
 * e.g., "Operational Dashboard Dashboard Tool" -> "Operational Dashboard"
 */
export const cleanToolName = (name: string): string => {
  // Order matters: check longer patterns first
  const suffixesToRemove = [' Dashboard Tool', ' Tool']

  let cleanedName = name
  for (const suffix of suffixesToRemove) {
    if (cleanedName.endsWith(suffix)) {
      cleanedName = cleanedName.slice(0, -suffix.length)
      break // Only remove one suffix
    }
  }

  return cleanedName.trim()
}

export interface IppToolNameResult {
  /** The human-readable tool name for display (e.g., "Campaigns & Offers") */
  ippToolName: string
  /** The external_id / route pattern (e.g., "retailer-funded-marketing-dashboard-tool") */
  externalId: string | undefined
  /** Whether the Totem entity query is still loading */
  loading: boolean
}

/**
 * Hook to derive the human-readable IPP tool name from Totem entity data.
 *
 * Priority order:
 * 1. metadata.ipp_tool_name_override (explicit override for display purposes)
 * 2. entity.name (the Totem entity's name, e.g., "Shortcuts Bar Tooling Dashboard Tool")
 * 3. Fallback to external_id (route pattern)
 * 4. Fallback to i18n "this tool"
 */
export const useIppToolName = (): IppToolNameResult => {
  const totemInfo = useTotemInfo()
  const externalId = totemInfo?.routePattern

  const messages = useDashMessages({
    fallbackToolName: 'common.thisTool',
  })

  const { data, loading } = useGetTotemEntityQuery({
    variables: { externalId: externalId ?? '' },
    skip: !externalId,
  })

  const entity = data?.instacart_infra_totem_v1_TotemService_GetEntity?.entity

  // Parse metadata to get ipp_tool_name_override if present
  const metadata = entity?.metadata as TotemEntityMetadata | null | undefined
  const ippToolNameOverride = metadata?.ipp_tool_name_override

  // The entity name (e.g., "Shortcuts Bar Tooling Dashboard Tool")
  const entityName = entity?.name

  // Priority: ipp_tool_name_override > entity.name > external_id > i18n fallback
  // Clean up redundant suffixes like "Dashboard Tool" or "Tool" from the name
  const rawName = ippToolNameOverride ?? entityName ?? externalId ?? messages.fallbackToolName
  const ippToolName = cleanToolName(rawName)

  return {
    ippToolName,
    externalId,
    loading,
  }
}
